import { z } from "zod";

/**
 * Auth form schemas (player Slice 1).
 *
 * These mirror Firebase's OWN rule only — they invent no backend policy and no
 * backend fields (the binding forbids inventing logic in the frontend):
 *   - email must be a valid email
 *   - password min length 8 (Firebase's floor; the demo login.html says "min 8")
 *   - sign-up requires confirm-password equality (a browser-side typo check)
 *
 * The backend create-profile call (Slice 2 onboarding) owns its own validation;
 * nothing here duplicates a backend field.
 */

const email = z
  .string()
  .min(1, "Enter your email address.")
  .email("Enter a valid email address.");

// Firebase's documented floor is 8 characters. We mirror it; we do not add a
// stricter policy of our own (no symbol/case/digit requirements).
const password = z.string().min(8, "Password must be at least 8 characters.");

export const signInSchema = z.object({
  email,
  // Sign-in only needs a non-empty password — the credential is checked by
  // Firebase. We do not re-enforce the min-8 rule on sign-in (a legacy account
  // could pre-date it); an empty field is the only client-side block.
  password: z.string().min(1, "Password is required."),
});

export const signUpSchema = z
  .object({
    accountName: z.string().trim().optional(),
    email,
    password,
    confirmPassword: z.string().min(1, "Re-enter your password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export const resetSchema = z.object({ email });

// The /reset-password landing form (new password + confirm). Reuses the same
// min-8 password rule as sign-up plus the confirm-equality typo check — no
// extra policy invented here either.
export const newPasswordSchema = z
  .object({
    password,
    confirmPassword: z.string().min(1, "Re-enter your new password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export type SignInValues = z.infer<typeof signInSchema>;
export type SignUpValues = z.infer<typeof signUpSchema>;
export type ResetValues = z.infer<typeof resetSchema>;
export type NewPasswordValues = z.infer<typeof newPasswordSchema>;
